import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import Warning from '@/images/WarningColored.svg';
import { useAnalytics } from '@/services/analytics';
import theme from '@/theme';
import { StyleCollection } from '@/utils/types';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useEffect } from 'react';
import Button from '../Buttons/Button';

interface ITexts {
    title: string;
    content: string;
    cta: string;
}

type Mode = 'circle' | 'square';

interface Props {
    open: boolean;
    isLoading: boolean;
    onDelete: () => Promise<void>;
    onClose: () => void;
    poster?: string;
    text: ITexts;
    mode: Mode;
    itemType: string;
    showWarningIcon?: boolean;
    mixpanelProp?: {
        key: string;
        properties: Record<string, unknown>;
    };
    onCancel?: () => void;
}

const Infographic = styled.img`
    width: 2.4rem;
    height: 2.4rem;
    margin-inline-end: 1rem;
`;
const ImageContainer = styled.div<{ mode: Mode }>`
    width: ${({ mode }) => (mode === 'square' ? '100%' : 'auto')};
    height: ${({ mode }) => (mode === 'circle' ? '20rem' : 'auto')};
    aspect-ratio: ${({ mode }) => (mode === 'circle' ? '1/1' : '2/1')};

    min-height: 5rem;

    & img {
        border-radius: ${({ mode }) => (mode === 'circle' ? '50%' : '8px')};
        object-fit: ${({ mode }) => (mode === 'circle' ? 'cover' : 'contain')};
    }
`;
const Image = styled.img`
    width: 100%;
    height: 100%;
`;

const styles: StyleCollection = {
    deleteButton: _theme => ({
        height: '4.4rem',
        padding: '0.5rem 2rem',
        fontSize: '1.6rem',
        alignItems: 'flex-center',
        flex: 1,
        borderRadius: '8px',
    }),
    closeIcon: theme => ({
        marginLeft: 'auto',
        color: theme.colors.primary,
    }),
    contentStyle: {
        flex: 1,
        overflow: 'hidden',
        alignItems: 'center',
    },
    cancelButton: {
        height: '4.4rem',
        padding: '0.5rem 2rem',
        fontSize: '1.6rem',
        alignItems: 'flex-start',
        borderRadius: '8px',
        flex: 1,
    },
};

const DialogSubtitle = styled(Typography)`
    line-height: 2.4rem;
    width: 100%;
    margin-bottom: 2rem;
    & span {
        text-decoration: underline;
        font-weight: bold;
    }
`;

const StyledIconButton = styled(IconButton)`
    color: ${theme.colors.secondary[900]};
`;

const StyledDialogTitle = styled(DialogTitle)`
    &.MuiDialogTitle-root {
        padding: 0;
    }
`;

const StyledDialogContent = styled(DialogContent)`
    &.MuiDialogContent-root {
        padding: 0;
    }
`;

const StyledDialogActions = styled(DialogActions)`
    &.MuiDialogActions-root {
        padding: 0;
    }
`;

const DELETE_MODAL_Z_INDEX = 1700;

export default function ({
    itemType,
    mode,
    text,
    open,
    onDelete,
    onClose,
    poster,
    isLoading,
    showWarningIcon = true,
    mixpanelProp,
    onCancel,
}: Props) {
    const mixpanel = useAnalytics();

    useEffect(() => {
        if (open) {
            const key = mixpanelProp?.key || `delete-${itemType}`;
            const properties = mixpanelProp?.properties || { event: 'view' };
            mixpanel?.track(key, properties);
        }
    }, [open]);

    return (
        <BootstrapDialog
            onClose={onClose}
            open={open}
            width="50rem"
            contentstyle={styles.contentStyle}
            PaperProps={{
                style: {
                    borderRadius: '12px',
                    gap: '16px',
                    padding: '24px 30px',
                    zIndex: DELETE_MODAL_Z_INDEX + 1,
                },
            }}
            slotProps={{
                root: {
                    style: { zIndex: DELETE_MODAL_Z_INDEX },
                } as any,
                backdrop: {
                    style: { zIndex: DELETE_MODAL_Z_INDEX },
                } as any,
            }}>
            <StyledDialogTitle id="delete-image-modal">
                {showWarningIcon && <Infographic src={Warning} />}
                <Typography variant="h2">{text.title}</Typography>
                <StyledIconButton ariaLabel="close" onClick={onClose} sx={styles.closeIcon}>
                    <CloseIcon fontSize="large" />
                </StyledIconButton>
            </StyledDialogTitle>
            <StyledDialogContent>
                <DialogSubtitle variant="body1">{text.content}</DialogSubtitle>
                {poster && (
                    <ImageContainer mode={mode}>
                        <Image src={poster} />
                    </ImageContainer>
                )}
            </StyledDialogContent>
            <StyledDialogActions>
                <Button
                    aria-label="cancel"
                    sx={styles.cancelButton}
                    onClick={() => {
                        onCancel?.();
                        onClose();
                    }}
                    variant="outlined"
                    color="secondary">
                    Cancel
                </Button>
                <Button
                    onClick={async () => {
                        await onDelete();
                        onClose();
                    }}
                    aria-label="delete"
                    color="error"
                    sx={styles.deleteButton}
                    isLoading={isLoading}
                    data-testid="delete-modal-delete-button">
                    {text.cta}
                </Button>
            </StyledDialogActions>
        </BootstrapDialog>
    );
}
